// Static data structures for the v6 Merchant Pricing Wizard (Step 2 of Onboarding).
// Sourced from Docs/Merchant_Onboarding_v6_Spec.md and the v5 HTML mockup.

// v6 — 9-stage flow matching Merchant_Onboarding_v6.html STAGES array exactly.
export const STAGES = [
  { id: 0, short: 'Sign-up', label: 'Sign-up + Master Info', time: '3 min', explainer: "Captures merchant identity, a 'Merchant Master Info' card (order source, category, pickup address, AOV, units/order, monthly shipment volume), and Payment & Credit (engagement model, average invoice value, COD remittance cycle, COD/prepaid split). Merchant identity freezes on submit; Master Info, Payment & Credit and Pickup stay editable." },
  { id: 1, short: 'KYC', label: 'KYC Documents', time: '4 min', explainer: 'Upload PAN, GSTIN, MSME/Udyam (optional), Cancelled cheque + bank proof, Authorised signatory KYC, and Address proof. Runs in parallel with the rest — only the final Commit is gated by KYC Approved.' },
  { id: 2, short: 'Profile & Service', label: 'Operating Profile + Service Type', time: '3 min', explainer: 'Freight direction, payment mode, mode-of-delivery × size, canonical service types.' },
  { id: 3, short: 'Commercials', label: 'Commercial Construct (Credit + Construct + Arrangement)', time: '5 min', explainer: 'Three cards in a continuous flow: (1) the recommended credit limit (postpaid engagements only — the engagement model itself is set at Sign-up); (2) the commercial construct (GST stance, volumetric coefficient, weight ceiling) entered manually; (3) Forward + RVP Arrangement (arrangement type, blended/per-courier, RVP mode).' },
  { id: 4, short: 'Rate Card', label: 'Rate Card (FWD + RVP)', time: '6 min', explainer: 'One rate-card workbook for Forward + RVP: courier-parent columns grouped Surface / Air / SDD / NDD, plus RVP / COD / RTO sheets as configured. Download, fill prices, upload — values fan out to every selected account code.' },
  { id: 5, short: 'T&Cs', label: 'Fuel Surcharge (DPH-Linked FSC)', time: '1 min', explainer: 'Configure the Diesel Price Hike (DPH) linked Fuel Surcharge for this merchant. When enabled, the FSC is dynamically applied based on the current diesel index.' },
  { id: 6, short: 'Review', label: 'Consolidated Review', time: '3 min', explainer: 'Non-editable final review of all inputs. Signed PDF on the left; generated consolidated rate card on the right (Forward + RVP if Independent). Banner if KYC pending blocks Commit.' },
  { id: 7, short: 'Commit', label: 'Pre-flight & Commit', time: '2 min', explainer: 'Pre-flight validation + native commit. Disabled when KYC !== Approved. On commit, persists forward rate card + RVP (formula metadata OR independent rate card) + audit log.' },
  { id: 8, short: 'Done', label: 'Confirmation', time: '<1 min', explainer: 'Email dispatched. Setup Confirmation Memo PDF filed. Merchant returns to Active state on the Listing page.' },
];

// KYC document definitions — matches HTML KYC_DOC_DEFS.
export const KYC_DOC_DEFS = [
  { key: 'pan', label: 'PAN Card', required: false, hasNumber: true, numLabel: 'PAN Number', numPlaceholder: 'AAAPL1234C' },
  { key: 'gstin', label: 'GSTIN', required: false, hasNumber: true, numLabel: 'GSTIN', numPlaceholder: '22AAAAA0000A1Z5' },
  { key: 'msme', label: 'MSME / Udyam', required: false, hasNumber: true, numLabel: 'MSME / Udyam Number', numPlaceholder: 'UDYAM-MH-01-0000000' },
  { key: 'bank', label: 'Cancelled Cheque · Bank Proof', required: false, bankProof: true },
  { key: 'signatory', label: 'Authorised Signatory KYC', required: false, signatory: true },
];

export const IFSC_BANKS = { HDFC: 'HDFC Bank', ICIC: 'ICICI Bank', SBIN: 'State Bank of India', AXIS: 'Axis Bank', PUNB: 'Punjab National Bank', KKBK: 'Kotak Mahindra Bank' };

// Arrangement types — matches HTML ARRANGEMENTS exactly.
export const ARRANGEMENTS = [
  { id: 'ZWZ', label: 'Zone-wise × Weight-slab-wise', sub: 'Different rate per zone per weight slab' },
  { id: 'ZAW', label: 'Zone-agnostic × Weight-slab-wise', sub: 'Flat across zones, varies by weight' },
  { id: 'ZAWA', label: 'Zone-agnostic × Weight-agnostic (with ceiling)', sub: 'Single flat rate. Mandatory weight ceiling' },
  { id: 'WAZ', label: 'Weight-agnostic × Zone-wise', sub: 'Flat per zone up to a zone-level ceiling' },
];

export const PAYMENT_LABELS = {
  PREPAID: 'Prepaid only shipments',
  COD: 'COD only shipments',
  BOTH: 'Both prepaid and COD shipments',
};

export const FREIGHT_DIRECTIONS = [
  { key: 'forward', label: 'Forward' },
  { key: 'reverse_noqc', label: 'Reverse · No QC' },
  { key: 'reverse_qc', label: 'Reverse · With QC' },
];

export const MODE_ROWS = [
  { k: 'surface', lbl: 'Surface' },
  { k: 'air', lbl: 'Air' },
  { k: 'sdd', lbl: 'SDD' },
  { k: 'sc_ndd', lbl: 'Same-City NDD' },
  { k: 'intercity_ndd', lbl: 'Inter-City NDD' },
  { k: 'h2to4', lbl: '2–4 hour delivery' },
  { k: 'sub2h', lbl: 'Sub-2 hour' },
];

export const SERVICE_TYPES = [
  { id: 'FWD_SURFACE_REG', family: 'Forward', sub: 'Surface · Regular' },
  { id: 'FWD_SURFACE_LARGE', family: 'Forward', sub: 'Surface · Large' },
  { id: 'FWD_AIR_REG', family: 'Forward', sub: 'Air · Regular' },
  { id: 'FWD_AIR_LARGE', family: 'Forward', sub: 'Air · Large' },
  { id: 'REV_REG_NOQC', family: 'Reverse', sub: 'Regular · No QC' },
  { id: 'REV_REG_QC', family: 'Reverse', sub: 'Regular · QC' },
  { id: 'REV_LARGE_NOQC', family: 'Reverse', sub: 'Large · No QC' },
  { id: 'REV_LARGE_QC', family: 'Reverse', sub: 'Large · QC' },
  { id: 'HV_FWD_REG', family: 'High Value', sub: 'Forward · Regular' },
  { id: 'HV_FWD_LARGE', family: 'High Value', sub: 'Forward · Large' },
  { id: 'HV_REVERSE_REG', family: 'High Value', sub: 'Reverse · Regular' },
  { id: 'HV_REVERSE_LARGE', family: 'High Value', sub: 'Reverse · Large' },
  { id: 'SDD_FWD_REG', family: 'SDD', sub: 'Forward · Regular' },
  { id: 'SDD_FWD_LARGE', family: 'SDD', sub: 'Forward · Large' },
  { id: 'SC_NDD_FWD_REG', family: 'NDD', sub: 'Same-City · Regular' },
  { id: 'SC_NDD_FWD_LARGE', family: 'NDD', sub: 'Same-City · Large' },
  { id: 'INTERCITY_NDD_REG', family: 'NDD', sub: 'Inter-City · Regular' },
  { id: 'INTERCITY_NDD_LARGE', family: 'NDD', sub: 'Inter-City · Large' },
  { id: 'H2TO4', family: 'Express', sub: '2–4 hour delivery' },
  { id: 'SUB2H', family: 'Express', sub: 'Sub-2 hour delivery' },
];

// Service-type codes are spelled inconsistently between this file and
// service_type_master: the master has `SC-NDD_FWD_REG` (hyphen) and
// `Intercity_NDD_REG` (title case) where we use `SC_NDD_FWD_REG` and
// `INTERCITY_NDD_REG`. Renaming our ids is not an option — merchants already
// store them in resolvedServiceTypes — so every lookup normalises instead.
// Normalising only upper-cases and folds '-' to '_', which keeps all master
// codes distinct from one another.
export function normalizeStCode(code) {
  return String(code || '').toUpperCase().replace(/-/g, '_');
}

// The serviceabilityGroups entry for a service id, matched on the normalised code.
export function findStGroup(groups, stId) {
  const want = normalizeStCode(stId);
  return (groups || []).find((g) => normalizeStCode(g.code) === want) || null;
}

// All serviceabilityGroups entries for a service id (a code can appear more than once).
export function findStGroups(groups, stId) {
  const want = normalizeStCode(stId);
  return (groups || []).filter((g) => normalizeStCode(g.code) === want);
}

// Courier `parent` is also inconsistently cased in the CP master
// (Xpressbees/XpressBees, ShadowFax/Shadowfax, Watsoo/WATSOO), so a rate map
// keyed by parent must be read case-insensitively or reverse accounts whose
// parent differs only in case silently lose their forward price.
export function ratesForParent(rates, parent) {
  if (!rates || !parent) return undefined;
  if (rates[parent] !== undefined) return rates[parent];
  const want = String(parent).toLowerCase();
  const hit = Object.keys(rates).find((k) => String(k).toLowerCase() === want);
  return hit !== undefined ? rates[hit] : undefined;
}


export const DEMO_MERCHANTS = {
  MileCollective: {
    label: 'MileCollective',
    description: 'Apparel · all-three directions · Both payment · ZWZ · Flat COD',
    signup: {
      business_name: 'Mile Collective Pvt Ltd', brand: 'Mile Collective',
      email: 'ops@milecollective.in', contact: '9810099999',
      address: 'A-44, 4th Floor, Sector 16', pincode: '201301',
      city: 'Noida', state: 'Uttar Pradesh', tnc: true,
    },
    masterInfo: {
      order_sources: ['Shopify', 'Instagram'],
      category: 'Apparel',
      pickup_address: { line1: 'A-44, Sector 16', line2: '4th Floor', city: 'Noida', state: 'Uttar Pradesh', pincode: '201301' },
      aov: 1500, units_per_order: 1.4, monthly_volume: 18000,
    },
    agreement: {
      filename: 'D2C Freight Commercial Proposal for MileCollective.pdf',
      gst_stance: 'exclusive', vc: 5000, weight_ceiling: 20000,
      detected_services: ['FWD_SURFACE_REG', 'REV_REG_NOQC', 'REV_REG_QC'],
    },
    profile: {
      directions: { forward: true, reverse_noqc: true, reverse_qc: true },
      payment: 'BOTH',
      modes: {
        surface: { reg: true, large: true }, air: { reg: false, large: false }, sdd: { reg: false, large: false },
        sc_ndd: { reg: false, large: false }, intercity_ndd: { reg: false, large: false },
        h2to4: { reg: false, large: false }, sub2h: { reg: false, large: false }, hv: { reg: false, large: false },
      },
    },
    extraTcsChips: [],
  },
  XMusxLuxury: {
    label: 'XMusx Luxury',
    description: 'Luxury D2C · all-three directions · Both payment · ZWZ · Flat COD (MAX ₹30, 1.5%)',
    signup: {
      business_name: 'XMusx Luxury Pvt Ltd', brand: 'XMusx Luxury',
      email: 'ops@xmusxluxury.in', contact: '9899988999',
      address: 'B-12, Hauz Khas Village', pincode: '110016',
      city: 'New Delhi', state: 'Delhi', tnc: true,
    },
    masterInfo: {
      order_sources: ['Shopify', 'Website'],
      category: 'Luxury',
      pickup_address: { line1: 'B-12, Hauz Khas Village', line2: '', city: 'New Delhi', state: 'Delhi', pincode: '110016' },
      aov: 8500, units_per_order: 1.2, monthly_volume: 4500,
    },
    agreement: {
      filename: 'XMusx Luxury Freight Commercial Proposal.pdf',
      gst_stance: 'exclusive', vc: 5000, weight_ceiling: 20000,
      detected_services: ['FWD_SURFACE_REG', 'FWD_SURFACE_LARGE', 'REV_REG_NOQC', 'REV_REG_QC'],
    },
    profile: {
      directions: { forward: true, reverse_noqc: true, reverse_qc: true },
      payment: 'BOTH',
      modes: {
        surface: { reg: true, large: true }, air: { reg: false, large: false }, sdd: { reg: false, large: false },
        sc_ndd: { reg: false, large: false }, intercity_ndd: { reg: false, large: false },
        h2to4: { reg: false, large: false }, sub2h: { reg: false, large: false }, hv: { reg: false, large: false },
      },
    },
    extraTcsChips: [
      { label: 'Max Liability', value: '₹10,000 per shipment' },
      { label: 'Insurance', value: 'Mandatory · 0.1% of invoice' },
      { label: 'Handling Charge', value: '₹15 per shipment' },
    ],
  },
};

// Order sources, grouped (sign-off 2026-05-29: added Storefront-platform + Social
// commerce groups; named marketplaces / POS refinement deferred).
export const ORDER_SOURCE_GROUPS = [
  { group: 'Storefront platforms', items: ['Shopify', 'WooCommerce', 'Magento', 'Wix', 'BigCommerce', 'Salesforce Commerce', 'Custom Website / API'] },
  { group: 'Social commerce', items: ['Instagram', 'Facebook Shops', 'WhatsApp'] },
  { group: 'Other channels', items: ['Marketplace', 'Offline', 'Other'] },
];
export const ORDER_SOURCES = ORDER_SOURCE_GROUPS.flatMap((g) => g.items);

// Proship integration — tech stack the integration sits on.
export const STOREFRONT_PLATFORMS = ['Shopify', 'WooCommerce', 'Magento', 'BigCommerce', 'Custom'];
export const OMS_PLATFORMS = ['Prozo WMS', 'UniCommerce', 'EasyEcom', 'Vinculum', 'Increff'];

// DPH-Linked FSC (diesel price hike) — config for the T&Cs sub-section.
export const DPH_MECHANISMS = [
  { id: 'percentage', label: 'Percentage-Linked', rateUnit: '% per 1% diesel rise', rateHint: 'e.g. 0.25 = 0.25% FSC for every 1% rise in diesel', formula: 'DPH % = ((D₁ − Base) / Base) × 100 × Hike Rate', appliedAs: '% on the selected charge components' },
  { id: 'rupee', label: 'Rupee-Linked', rateUnit: '₹ per ₹1 diesel rise', rateHint: 'e.g. 0.30 = ₹0.30 surcharge for every ₹1 rise in diesel', formula: 'DPH ₹ = (D₁ − Base) × Hike Rate', appliedAs: 'flat ₹ per shipment (does not scale with freight)' },
];

export const DPH_CHARGES = [
  { key: 'forward', label: 'Forward Freight', note: 'Per-shipment forward leg charge.' },
  { key: 'rto', label: 'RTO Freight', note: 'Return-to-origin freight on undelivered shipments.' },
  { key: 'rvp', label: 'RVP Freight', note: 'Reverse pickup freight on customer-initiated returns.' },
  { key: 'cod', label: 'COD Charges', note: 'COD handling fee.' },
  { key: 'qc', label: 'QC Charges', note: 'Quality check / inspection charges on returns.' },
  { key: 'vas', label: 'Communication & other VAS', note: 'Delivery OTP, DWS image charges, etc.' },
];
// Merchant size tiers by annual revenue (shown in the Category dropdown).
export const CATEGORIES = [
  { value: 'MICRO', label: 'Micro Enterprise', sub: '< ₹10 Cr annual revenue' },
  { value: 'SMALL', label: 'Small Enterprise', sub: '₹10–100 Cr annual revenue' },
  { value: 'MEDIUM', label: 'Medium Enterprise', sub: '₹100–500 Cr annual revenue' },
  { value: 'LARGE', label: 'Large Enterprise', sub: '> ₹500 Cr annual revenue' },
];

// Matches HTML PROZO_WAREHOUSES (id + name).
export const PROZO_WAREHOUSES = [
  { code: 'WH-BLR-01', label: 'Bangalore · Bommasandra' },
  { code: 'WH-MUM-01', label: 'Mumbai · Bhiwandi' },
  { code: 'WH-DEL-01', label: 'Delhi NCR · Manesar' },
  { code: 'WH-HYD-01', label: 'Hyderabad · Patancheru' },
  { code: 'WH-KOL-01', label: 'Kolkata · Howrah' },
];

export const ENGAGEMENT_MODELS = [
  { id: 'prepaid', label: 'Prepaid', sub: 'Merchant pays Prozo upfront. Lowest credit risk.' },
  { id: 'postpaid', label: 'Postpaid', sub: 'Net-30/45/60 credit. Recommended for established merchants.' },
  { id: 'secured_postpaid', label: 'Secured Postpaid', sub: 'For COD-heavy merchants — service fee deducted from COD remittance.' },
];

export const ARRANGEMENT_TYPES = [
  { id: 'ZxW', label: 'Zone × Weight', sub: 'Standard rate matrix — different rate per zone × slab.' },
  { id: 'ZWZ', label: 'Zone-agnostic, Weight-slab', sub: 'Same rate across zones but varies by weight slab.' },
  { id: 'ZAWS', label: 'Zone-agnostic, Weight-slab', sub: 'Alias of ZWZ for legacy contracts.' },
  { id: 'ZAWA', label: 'Zone-agnostic, Weight-agnostic', sub: 'Flat rate per shipment regardless of zone or weight (weight ceiling applies).' },
  { id: 'WAZ', label: 'Weight-agnostic, Zone-wise', sub: 'Same rate across weights but varies by zone.' },
];

export function derivedServicesForMode(mode, reg, large) {
  const map = {
    surface: ['FWD_SURFACE_REG', 'FWD_SURFACE_LARGE'],
    air: ['FWD_AIR_REG', 'FWD_AIR_LARGE'],
    sdd: ['SDD_FWD_REG', 'SDD_FWD_LARGE'],
    sc_ndd: ['SC_NDD_FWD_REG', 'SC_NDD_FWD_LARGE'],
    intercity_ndd: ['INTERCITY_NDD_REG', 'INTERCITY_NDD_LARGE'],
    h2to4: ['H2TO4', 'H2TO4'],
    sub2h: ['SUB2H', 'SUB2H'],
  };
  const arr = [];
  if (reg) arr.push(map[mode][0]);
  if (large && map[mode][1] !== map[mode][0]) arr.push(map[mode][1]);
  return arr;
}

export function resolveServicesFromProfile(profile) {
  const out = [];
  const modes = profile.modes;
  const isHigh = profile.shipment_value === 'high';
  const pushSvc = (id, derivation) => {
    const meta = SERVICE_TYPES.find((s) => s.id === id) || { id, family: 'Other', sub: '' };
    out.push({ ...meta, included: true, derivation });
  };
  if (profile.directions.forward) {
    if (modes.surface.reg) pushSvc('FWD_SURFACE_REG', 'Forward + Surface + Regular');
    if (modes.surface.large) pushSvc('FWD_SURFACE_LARGE', 'Forward + Surface + Large');
    if (modes.air.reg) pushSvc('FWD_AIR_REG', 'Forward + Air + Regular');
    if (modes.air.large) pushSvc('FWD_AIR_LARGE', 'Forward + Air + Large');
    if (modes.sdd.reg) pushSvc('SDD_FWD_REG', 'Forward + SDD + Regular');
    if (modes.sdd.large) pushSvc('SDD_FWD_LARGE', 'Forward + SDD + Large');
    if (modes.sc_ndd.reg) pushSvc('SC_NDD_FWD_REG', 'Forward + Same-City NDD + Regular');
    if (modes.sc_ndd.large) pushSvc('SC_NDD_FWD_LARGE', 'Forward + Same-City NDD + Large');
    if (modes.intercity_ndd.reg) pushSvc('INTERCITY_NDD_REG', 'Forward + Inter-City NDD + Regular');
    if (modes.intercity_ndd.large) pushSvc('INTERCITY_NDD_LARGE', 'Forward + Inter-City NDD + Large');
    if (modes.h2to4.reg || modes.h2to4.large) pushSvc('H2TO4', 'Forward + 2–4h Express');
    if (modes.sub2h.reg || modes.sub2h.large) pushSvc('SUB2H', 'Forward + Sub-2h Express');
    if (isHigh) pushSvc('HV_FWD_REG', 'High-Value (by shipment value) + Forward');
  }
  if (profile.directions.reverse_noqc) {
    if (modes.surface.reg || modes.surface.large || modes.air.reg) pushSvc('REV_REG_NOQC', 'Reverse + Regular + No QC');
    if (modes.surface.large) pushSvc('REV_LARGE_NOQC', 'Reverse + Large + No QC');
  }
  if (profile.directions.reverse_qc) {
    if (modes.surface.reg || modes.surface.large || modes.air.reg) pushSvc('REV_REG_QC', 'Reverse + Regular + With QC');
    if (modes.surface.large) pushSvc('REV_LARGE_QC', 'Reverse + Large + With QC');
  }
  if (isHigh && (profile.directions.reverse_noqc || profile.directions.reverse_qc)) {
    pushSvc('HV_REVERSE_REG', 'High-Value (by shipment value) + Reverse');
  }
  return out;
}

export const FAMILIES_BY_DIRECTION = (directions, shipmentValue) => {
  const allowed = new Set();
  if (directions.forward) ['Forward', 'SDD', 'NDD', 'Express'].forEach((f) => allowed.add(f));
  if (directions.reverse_noqc || directions.reverse_qc) allowed.add('Reverse');
  if (shipmentValue === 'high' && (directions.forward || directions.reverse_noqc || directions.reverse_qc)) {
    allowed.add('High Value');
  }
  return [...allowed];
};

export const ZONES = [
  { id: 1, name: 'Local' },
  { id: 2, name: 'Regional' },
  { id: 3, name: 'Metro' },
  { id: 4, name: 'ROI' },
  { id: 5, name: 'Special' },
];

export const STANDARD_SLABS_GMS = [250, 500, 1000, 1500, 2000, 2500, 5000, 10000];

// Indian states / UTs → GST state code (first 2 digits of GSTIN).
export const STATE_GST_CODES = {
  'Jammu & Kashmir': '01', 'Himachal Pradesh': '02', 'Punjab': '03', 'Chandigarh': '04',
  'Uttarakhand': '05', 'Haryana': '06', 'Delhi': '07', 'Rajasthan': '08',
  'Uttar Pradesh': '09', 'Bihar': '10', 'Sikkim': '11', 'Arunachal Pradesh': '12',
  'Nagaland': '13', 'Manipur': '14', 'Mizoram': '15', 'Tripura': '16',
  'Meghalaya': '17', 'Assam': '18', 'West Bengal': '19', 'Jharkhand': '20',
  'Odisha': '21', 'Chhattisgarh': '22', 'Madhya Pradesh': '23', 'Gujarat': '24',
  'Dadra & Nagar Haveli and Daman & Diu': '26', 'Maharashtra': '27', 'Karnataka': '29',
  'Goa': '30', 'Lakshadweep': '31', 'Kerala': '32', 'Tamil Nadu': '33',
  'Puducherry': '34', 'Andaman & Nicobar Islands': '35', 'Telangana': '36',
  'Andhra Pradesh': '37', 'Ladakh': '38', 'Other Territory': '97',
};
export const INDIAN_STATES = Object.keys(STATE_GST_CODES);
export const gstStateCode = (state) => STATE_GST_CODES[state] || '';

// 15-char GSTIN: 2-digit state + 5 letters + 4 digits + 1 letter + 1 alnum + 'Z' + 1 alnum.
export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
export const isValidGstinFormat = (gstin) => GSTIN_REGEX.test((gstin || '').toUpperCase().trim());
export const gstinMatchesState = (gstin, state) => {
  const code = gstStateCode(state);
  if (!code) return false;
  return isValidGstinFormat(gstin) && (gstin || '').toUpperCase().slice(0, 2) === code;
};

